import { Injectable } from '@angular/core';
import Dexie from 'dexie';
import moment from 'moment';
import { LOG_LEVEL } from '../../enums/log-level.enum';
import { LogEntry } from './log-entry';
import { EncryptionService } from '@evorait/pace-encryption';
import { firstValueFrom, take } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable()
export class LogDexieService extends Dexie {
    public static readonly DB_NAME = 'LogDatabase';
    public static readonly DB_TABLE = 'logentries';
    public static readonly DB_KEY_PATH = 'id';

    private static readonly VERSION_ONE = 1;
    private static readonly VERSION_TWO = LogDexieService.VERSION_ONE + 1;

    private logentries: Dexie.Table<LogEntry, number>;

    private checkedForOldEntries = false;
    private pendingEncryptionQueue: LogEntry[] = [];

    public constructor(private readonly encryption: EncryptionService) {
        super(LogDexieService.DB_NAME);
        this.version(LogDexieService.VERSION_ONE).stores({
            [LogDexieService.DB_TABLE]: 'id++,timestamp,level,message',
        });

        // encrypt potentially existing data
        this.version(LogDexieService.VERSION_TWO).upgrade(async (trans) => {
            await firstValueFrom(this.encryption.selectInitialized.pipe(filter(Boolean)));
            return trans
                .table(LogDexieService.DB_TABLE)
                .toCollection()
                .modify(async (logEntry: LogEntry) => {
                    try {
                        const currentMessage = logEntry.message;
                        if (!currentMessage.startsWith(EncryptionService.PREFIX_ENCRYPTED_CONTENT)) {
                            logEntry.message = this.encryption.encrypt(currentMessage);
                        }
                    } catch {
                        console.error(
                            `LogDexieService (DB version upgrade): Failed to upgrade log message ${logEntry.id} to version ${LogDexieService.VERSION_TWO}`,
                        );
                    }
                });
        });

        this.logentries.mapToClass(LogEntry);
        this.handleEncryptionDelay();
    }

    /**
     * Waits for the encryption service to be initialised and
     * then encrypts and stores all log messages that could not be handled beforehand.
     */
    private handleEncryptionDelay(): void {
        this.encryption.selectInitialized.pipe(filter(Boolean), take(1)).subscribe(() => {
            this.pendingEncryptionQueue.forEach((logEntry) => {
                this.logentries.add({ ...logEntry, message: this.encryption.encrypt(logEntry.message) }).catch((e) => {
                    console.error(
                        `${this.constructor.name}: Failed to add log entry to database: ${JSON.stringify(e)}`,
                    );
                });
            });
            this.pendingEncryptionQueue = [];
        });
    }

    public get pendingQueueLength(): number {
        return this.pendingEncryptionQueue.length;
    }

    public async getAllLogEntries(): Promise<LogEntry[]> {
        try {
            const logEntries = await this.logentries.orderBy('timestamp').toArray();
            logEntries.forEach((logEntry) => (logEntry.message = this.encryption.decrypt(logEntry.message)));
            return logEntries;
        } catch (error) {
            console.error(`LogDexieService: getAllLogEntries: Error on reading log entries from database: ${error}`);
            return [];
        }
    }

    /**
     * Writes a single log message to the database. The message should contain any required information in a single string.
     *
     * @param message the log message to be written
     * @param level the log level of the message
     * @returns a promise that resolves when the message has been written to the database
     */
    public async writeEntry(message: string, level: LOG_LEVEL): Promise<void> {
        let start = Promise.resolve();
        const dexieLogRetainingPeriod = 14;
        if (!this.checkedForOldEntries) {
            start = this.purgeEntriesOlderThanDays(dexieLogRetainingPeriod).then(() => {
                this.checkedForOldEntries = true;
            });
        }
        await start;
        if (!this.encryption.isInitialized) {
            this.pendingEncryptionQueue.push({
                timestamp: moment().toDate().getTime(),
                level: level,
                message: message,
            });
            return;
        }
        message = this.encryption.encrypt(message);
        try {
            await this.logentries.add({
                timestamp: moment().toDate().getTime(),
                level: level,
                message: message,
            });
        } catch (error) {
            console.error(`LogDexieService: writeEntry: Error on writing log entry to database: ${error}`);
        }
    }

    /**
     * Deletes all log entries from the database. Especially used for unit tests to produce a well defined starting state.
     *
     * @returns {Promise<void>} A promise that resolves when the entries have been cleared.
     */
    public async clearEntries(): Promise<void> {
        try {
            return await this.logentries.clear();
        } catch (error) {
            console.error(`LogDexieService: clearEntries: Error on clearing the database: ${error}`);
        }
    }

    public purgeEntriesOlderThanDays(days: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.logentries
                .where('timestamp')
                .belowOrEqual(this.convertDaysToMilliseconds(days))
                .delete()
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    const errorMessage = `LogDexieService: purgeEntriesOlderThanDays: Could not delete entries from database older than: ${days}. Error ${error}`;
                    reject(errorMessage);
                });
        });
    }

    public convertDaysToMilliseconds(days: number): number {
        // Used toDate().getTime() here on purpose to allow unit tests to manipulate the clock.
        const now = moment().toDate().getTime();
        const daysInMilliseconds = moment.duration(days, 'day').asMilliseconds();
        return now - daysInMilliseconds;
    }
}
